import { computed, defineComponent, h } from 'vue'
import { QIcon } from 'quasar'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { domHandlers } from '../quasarHandlers'

/**
 * The status pill on a campaign or a book: one of four states, or a bare coloured dot in `round` mode.
 *
 * NOTE, carried over: `icon` returns the string 'nonexistant' for active and draft — QIcon renders
 * nothing for an unknown name, which is how those two end up with no icon. And an unrecognised state
 * reads 'Unknown' rather than being left blank.
 */
export default defineComponent({
  name: 'BsStatus',

  /**
   * Declared so `input` is an emitted event rather than a fallthrough attribute: undeclared, the
   * `onInput` the render attaches stays in `$attrs` and Vue applies it to the root element too, so a
   * single DOM input event ran the handler twice.
   */
  emits: ['input'],

  props: {
    ...bsComponentProps,
    borderColor: { type: String, default: void 0 },
    round: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    draft: { type: Boolean, default: false },
    finished: { type: Boolean, default: false },
    scheduled: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    const bs = useBsComponent({ vm: useVm(), props, emit })

    const status = computed(() => props.active ? 'Active'
      : props.draft ? 'Draft'
        : props.finished ? 'Finished'
          : props.scheduled ? 'Scheduled'
            : props.round ? void 0
              : 'Unknown')

    const icon = computed(() => props.active ? 'nonexistant'
      : props.draft ? 'nonexistant'
        : props.finished ? 'icon-check'
          : props.scheduled ? 'icon-clock'
            : props.round ? void 0
              : 'Unknown')

    return { ...bs, status, icon }
  },

  render () {
    return h('div', {
      class: ['bs-status', this.getClasses({
        'bs-status--round': this.round,
        'bs-status--active': this.active,
        'bs-status--draft': this.draft,
        'bs-status--finished': this.finished,
        'bs-status--scheduled': this.scheduled
      })],
      style: this.borderColor === void 0 ? void 0 : 'border-color: ' + this.borderColor,
      ...domHandlers(this.getListeners())
    }, [
      h(QIcon, {
        name: this.icon
      }),
      h('span', [
        this.status,
        this.slot(this, 'default')
      ])
    ])
  }
})
